import CallMadeRounded from '@mui/icons-material/CallMadeRounded';
import CallReceivedRounded from '@mui/icons-material/CallReceivedRounded';
import CurrencyExchangeRounded from '@mui/icons-material/CurrencyExchangeRounded';
import PaymentsRounded from '@mui/icons-material/PaymentsRounded';
import SavingsRounded from '@mui/icons-material/SavingsRounded';
import ShowChartRounded from '@mui/icons-material/ShowChartRounded';
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router';
import { api, unwrap, unwrapJson } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { DataTable, SearchField, type Column } from '../../components/DataTable';
import { Money } from '../../components/Money';
import { EmptyState, PageHeader } from '../../components/states';
import { StatusChip } from '../../components/StatusChip';
import { businessDate, money, moneyCompact } from '../../lib/format';
import { useShop } from '../../shop/ShopProvider';
import { percent } from './allocation';
import { keys, type PaymentRow } from './kit/hooks';
import { PURPOSES, type Purpose } from './kit/labels';
import { METHOD_ICONS } from './kit/MethodSelect';
import { AllocationBar, Cell2, DateWithParty, ErrorState, StatCard, StatGrid } from './kit/ui';
import { MethodsTab } from './MethodsTab';
import { useExportSelection } from '../dataio/useExportSelection';
import { filesColumn, useAttachmentCounts } from '../attachments';

const FILTERS: { value: '' | Purpose; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'RECEIPT', label: 'Received' },
  { value: 'SUPPLIER_PAYMENT', label: 'Paid to suppliers' },
  { value: 'CUSTOMER_REFUND', label: 'Customer refunds' },
  { value: 'SUPPLIER_REFUND', label: 'Supplier refunds' },
];

export function PurposeChip({ purpose }: { purpose: Purpose }) {
  const p = PURPOSES[purpose];
  const refund = purpose === 'CUSTOMER_REFUND' || purpose === 'SUPPLIER_REFUND';
  return (
    <Chip
      size="small"
      icon={p.inflow ? <CallReceivedRounded /> : <CallMadeRounded />}
      label={p.label}
      sx={(t) => {
        const c = (t.vars ?? t).palette[refund ? 'info' : p.inflow ? 'success' : 'warning'].main;
        return {
          bgcolor: `color-mix(in srgb, ${c} 10%, transparent)`,
          color: c,
          border: `1px solid color-mix(in srgb, ${c} 20%, transparent)`,
          '& .MuiChip-icon': { color: c, fontSize: 15 },
        };
      }}
    />
  );
}

export function PaymentsPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'methods' ? 'methods' : 'payments';
  const { can } = useAuth();
  const navigate = useNavigate();

  return (
    <>
      <PageHeader
        title="Payments"
        subtitle="Money received from customers, paid to suppliers, and refunds — and what each one settled."
        actions={
          can('payments.create') && (
            <>
              <Button
                variant="outlined"
                startIcon={<CallMadeRounded />}
                onClick={() => void navigate('/payments/new?purpose=SUPPLIER_PAYMENT')}
              >
                Pay supplier
              </Button>
              <Button
                variant="contained"
                startIcon={<CallReceivedRounded />}
                onClick={() => void navigate('/payments/new?purpose=RECEIPT')}
              >
                Receive payment
              </Button>
            </>
          )
        }
      />
      <Tabs
        value={tab}
        onChange={(_, v: string) => setParams(v === 'methods' ? { tab: 'methods' } : {})}
        sx={{ mb: 2.5, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="payments" label="Payments" />
        <Tab value="methods" label="Payment methods" />
      </Tabs>
      {tab === 'payments' ? <PaymentsList /> : <MethodsTab />}
    </>
  );
}

function PaymentsList() {
  const exportSelection = useExportSelection('payment');
  const [params, setParams] = useSearchParams();
  const { can } = useAuth();
  const { format } = useShop();
  const navigate = useNavigate();
  const purpose = (params.get('purpose') ?? '') as '' | Purpose;
  const status = (params.get('status') ?? '') as '' | 'POSTED' | 'VOIDED';
  const q = params.get('q') ?? '';
  const page = Number(params.get('page') ?? '1') || 1;
  const pageSize = Number(params.get('pageSize') ?? '25') || 25;

  const set = (patch: Record<string, string | number | null>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          if (v === null || v === '') next.delete(k);
          else next.set(k, String(v));
        }
        if (!('page' in patch)) next.delete('page');
        return next;
      },
      { replace: true },
    );

  const list = useQuery({
    queryKey: [...keys.payments, 'list', { purpose, status, q, page, pageSize }],
    queryFn: () =>
      unwrap(
        api.GET('/payments', {
          params: {
            query: {
              page,
              pageSize,
              q: q || undefined,
              purpose: purpose || undefined,
              status: status || undefined,
            },
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });

  const report = useQuery({
    queryKey: ['reports', 'payments', 'month'],
    queryFn: () => unwrapJson(api.GET('/reports/payments')),
    enabled: can('reports.view'),
  });

  const fileCounts = useAttachmentCounts(
    'PAYMENT',

    (list.data?.data ?? []).map((r) => r.id),
  );

  const columns: Column<PaymentRow>[] = [
    {
      key: 'number',
      header: 'Payment',
      render: (r) => (
        <Cell2
          primary={
            <Box
              component="span"
              sx={{ textDecoration: r.status === 'VOIDED' ? 'line-through' : undefined }}
            >
              {r.number}
            </Box>
          }
          secondary={
            <DateWithParty date={businessDate(r.paymentDate, format)} party={r.contact.name} />
          }
        />
      ),
    },
    {
      key: 'party',
      header: 'Party',
      hideBelow: 'sm',
      render: (r) => <Cell2 primary={r.contact.name} secondary={r.reference ?? r.contact.phone} />,
    },
    {
      key: 'purpose',
      header: 'Purpose',
      hideBelow: 'md',
      render: (r) => <PurposeChip purpose={r.purpose} />,
    },
    {
      key: 'method',
      header: 'Method',
      hideBelow: 'lg',
      render: (r) => (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', color: 'text.secondary' }}>
          {METHOD_ICONS[r.method.kind]}
          <Typography variant="body2" color="text.primary">
            {r.method.name}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'allocated',
      header: 'Allocated',
      hideBelow: 'sm',
      width: 180,
      render: (r) =>
        r.status === 'VOIDED' ? (
          <Typography variant="caption" color="text.disabled">
            Reversed
          </Typography>
        ) : (
          <Box>
            <AllocationBar
              value={percent(r.allocated, r.amount)}
              tone={Number(r.unallocated) > 0 ? 'warning' : 'success'}
            />
            <Typography variant="caption" color="text.secondary" className="tabular">
              {Number(r.unallocated) > 0
                ? `${money(r.unallocated, format)} unallocated`
                : 'Fully allocated'}
            </Typography>
          </Box>
        ),
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (r) => (
        <Box
          sx={{
            color:
              r.status === 'VOIDED'
                ? 'text.disabled'
                : r.direction === 'IN'
                  ? 'success.main'
                  : 'text.primary',
            textDecoration: r.status === 'VOIDED' ? 'line-through' : undefined,
          }}
        >
          {r.direction === 'IN' ? '+' : '−'}
          <Money value={r.amount} strong />
        </Box>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      hideBelow: 'sm',
      render: (r) => (
        <StatusChip status={r.status} label={r.status === 'VOIDED' ? 'Voided' : 'Posted'} />
      ),
    },
  ];

  const filtered = Boolean(purpose || status || q);
  const t = report.data?.totals;

  return (
    <>
      {can('reports.view') && (
        <StatGrid>
          <StatCard
            label="Money in · this month"
            value={t && moneyCompact(t.moneyIn, format)}
            hint="Receipts and supplier refunds"
            icon={<SavingsRounded />}
            tone="success"
            loading={report.isLoading}
          />
          <StatCard
            label="Money out · this month"
            value={t && moneyCompact(t.moneyOut, format)}
            hint="Supplier payments and refunds"
            icon={<PaymentsRounded />}
            tone="warning"
            loading={report.isLoading}
          />
          <StatCard
            label="Net cash flow"
            value={t && moneyCompact(t.net, format)}
            hint={t && (Number(t.net) >= 0 ? 'More in than out' : 'More out than in')}
            icon={<ShowChartRounded />}
            tone={t && Number(t.net) < 0 ? 'error' : 'primary'}
            loading={report.isLoading}
          />
          <StatCard
            label="Payments · this month"
            value={t?.count}
            hint="Posted, excluding voids"
            icon={<CurrencyExchangeRounded />}
            tone="info"
            loading={report.isLoading}
          />
        </StatGrid>
      )}

      {list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : (
        <>
          <DataTable
            {...exportSelection.table}
            columns={[...columns, ...filesColumn<PaymentRow>(fileCounts)]}
            rows={list.data?.data}
            rowKey={(r) => r.id}
            loading={list.isLoading}
            page={list.data ? { page, pageSize, total: list.data.page.total } : undefined}
            onPageChange={(p) => set({ page: p })}
            onPageSizeChange={(s) => set({ pageSize: s, page: null })}
            onRowClick={(r) => void navigate(`/payments/${r.id}`)}
            toolbar={
              <>
                <SearchField
                  value={q}
                  onChange={(v) => set({ q: v })}
                  placeholder="Search number, party or reference"
                />
                <Stack
                  direction="row"
                  spacing={0.75}
                  sx={{ flexWrap: 'wrap', rowGap: 0.75, flex: 1 }}
                >
                  {FILTERS.map((f) => (
                    <Chip
                      key={f.value || 'all'}
                      label={f.label}
                      clickable
                      color={purpose === f.value ? 'primary' : 'default'}
                      variant={purpose === f.value ? 'filled' : 'outlined'}
                      onClick={() => set({ purpose: f.value })}
                    />
                  ))}
                </Stack>
                <TextField
                  select
                  size="small"
                  value={status}
                  onChange={(e) => set({ status: e.target.value })}
                  fullWidth={false}
                  sx={{ minWidth: 140, maxWidth: { sm: 200 } }}
                  slotProps={{
                    select: { displayEmpty: true },
                    htmlInput: { 'aria-label': 'Status' },
                  }}
                >
                  <MenuItem value="">Any status</MenuItem>
                  <MenuItem value="POSTED">Posted</MenuItem>
                  <MenuItem value="VOIDED">Voided</MenuItem>
                </TextField>
              </>
            }
            empty={
              filtered ? (
                <EmptyState
                  icon={<PaymentsRounded />}
                  title="No matching payments"
                  body="Try another search or clear the filters."
                  action={{
                    label: 'Clear filters',
                    onClick: () => setParams({}, { replace: true }),
                  }}
                />
              ) : (
                <EmptyState
                  icon={<PaymentsRounded />}
                  title="No payments yet"
                  body="Record money a customer pays you, or a payment to a supplier. You choose which invoices it settles."
                  action={
                    can('payments.create')
                      ? {
                          label: 'Receive payment',
                          onClick: () => void navigate('/payments/new?purpose=RECEIPT'),
                        }
                      : undefined
                  }
                />
              )
            }
          />
          {exportSelection.dialog}
        </>
      )}
    </>
  );
}
